import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  FileBarChart,
  LogOut,
  Moon,
  Sun,
  X,
  AlertTriangle,
  BarChart3,
  Users,
  CreditCard,
  Activity,
  DollarSign,
  Settings,
  FileText,
  Tag,
  Gift,
  MessageSquare,
  Wallet,
  Hash,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/i18n';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  variant: 'user' | 'admin';
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  separator?: boolean; // Show separator after this item
}

const adminNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/admin/dashboard',
    icon: <BarChart3 className="w-5 h-5" />,
  },
  {
    label: 'Usuarios',
    path: '/admin/users',
    icon: <Users className="w-5 h-5" />,
  },
  {
    label: 'Planos',
    path: '/admin/plans',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    label: 'Codigos VIP',
    path: '/admin/promo-codes',
    icon: <Tag className="w-5 h-5" />,
  },
  {
    label: 'Indicacoes',
    path: '/admin/referrals',
    icon: <Gift className="w-5 h-5" />,
  },
  {
    label: 'Uso',
    path: '/admin/usage',
    icon: <Activity className="w-5 h-5" />,
  },
  {
    label: 'Custos',
    path: '/admin/costs',
    icon: <DollarSign className="w-5 h-5" />,
  },
  {
    label: 'Billing',
    path: '/admin/billing',
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    label: 'Feedbacks',
    path: '/admin/feedbacks',
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    label: 'Erros',
    path: '/admin/errors',
    icon: <AlertTriangle className="w-5 h-5" />,
  },
  {
    label: 'Configuracoes',
    path: '/admin/settings',
    icon: <Settings className="w-5 h-5" />,
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, variant }) => {
  const { isDarkMode, toggleDarkMode, logout, adminLogout } = useAuthStore();
  const { t } = useTranslation();

  const userNavItems: NavItem[] = [
    // Overview
    {
      label: t.app.sidebar.dashboard,
      path: '/app/dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      separator: true,
    },
    // Money
    {
      label: t.app.sidebar.transactions,
      path: '/app/transactions',
      icon: <Receipt className="w-5 h-5" />,
    },
    {
      label: t.app.sidebar.pots,
      path: '/app/pots',
      icon: <Wallet className="w-5 h-5" />,
      separator: true,
    },
    // Planning
    {
      label: t.app.sidebar.budgets,
      path: '/app/budgets',
      icon: <DollarSign className="w-5 h-5" />,
    },
    {
      label: t.app.sidebar.recurring,
      path: '/app/recurring',
      icon: <Receipt className="w-5 h-5" />,
      separator: true,
    },
    // Analytics
    {
      label: t.app.sidebar.reports,
      path: '/app/reports',
      icon: <FileBarChart className="w-5 h-5" />,
      separator: true,
    },
    // Configuration
    {
      label: t.app.sidebar.categories,
      path: '/app/categories',
      icon: <Tag className="w-5 h-5" />,
    },
    {
      label: t.app.sidebar.tags,
      path: '/app/tags',
      icon: <Hash className="w-5 h-5" />,
    },
    {
      label: t.app.sidebar.settings,
      path: '/app/settings',
      icon: <Settings className="w-5 h-5" />,
    },
  ];

  const navItems = variant === 'admin' ? adminNavItems : userNavItems;
  const handleLogout = variant === 'admin' ? adminLogout : logout;
  const logoutPath = variant === 'admin' ? '/admin/login' : '/auth';

  const handleLogoutClick = () => {
    handleLogout();
    window.location.href = logoutPath;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64
          bg-white dark:bg-secondary-800
          border-r border-gray-200 dark:border-secondary-700
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-secondary-700">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                KeepNota
              </span>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Badge for admin */}
          {variant === 'admin' && (
            <div className="px-4 py-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                Admin Panel
              </span>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <React.Fragment key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    font-medium text-sm transition-colors duration-200
                    ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/50 dark:text-primary-400'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-secondary-700'
                    }
                  `}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
                {item.separator && (
                  <div className="my-2 border-t border-gray-200 dark:border-secondary-700" />
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-secondary-700 space-y-1">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-secondary-700
                font-medium text-sm transition-colors duration-200"
            >
              {isDarkMode ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
              {isDarkMode ? t.app.sidebar.lightMode : t.app.sidebar.darkMode}
            </button>

            {/* Logout */}
            <button
              onClick={handleLogoutClick}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20
                font-medium text-sm transition-colors duration-200"
            >
              <LogOut className="w-5 h-5" />
              {t.app.sidebar.logout}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
